import type { NodeMetrics } from '@/types'
import { formatKstTime } from '@/utils/datetime'
import MetricBar, { formatBytes, formatPercent, metricColor } from './MetricBar'

interface Props {
  metrics: NodeMetrics | null
}

function usedFromPercent(totalBytes: number | null | undefined, percent: number | null | undefined): number | null {
  if (totalBytes == null || percent == null) return null
  return totalBytes * (percent / 100)
}

function diskUsedBytes(metrics: NodeMetrics): number | null {
  if (metrics.disk_total_bytes != null && metrics.disk_free_bytes != null) {
    return Math.max(metrics.disk_total_bytes - metrics.disk_free_bytes, 0)
  }
  return usedFromPercent(metrics.disk_total_bytes, metrics.disk_percent)
}

function memoryDetail(metrics: NodeMetrics): string {
  if (metrics.memory_total_bytes == null) return 'Total -'
  const used = usedFromPercent(metrics.memory_total_bytes, metrics.memory_percent)
  if (used == null) return `Total ${formatBytes(metrics.memory_total_bytes)}`
  return `${formatBytes(used)} used of ${formatBytes(metrics.memory_total_bytes)}`
}

function Bar({ value }: { value: number | null | undefined }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1 }}>
        <MetricBar value={value} />
      </div>
      <span style={{ fontSize: 12, color: '#555', minWidth: 36, textAlign: 'right' }}>
        {formatPercent(value)}
      </span>
    </div>
  )
}

function MetricStat({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color?: string
}) {
  return (
    <div style={metricStatStyle}>
      <div style={metricStatLabelStyle}>{label}</div>
      <div style={{ ...metricStatValueStyle, color: color ?? metricStatValueStyle.color }}>
        {value}
      </div>
    </div>
  )
}

function DiskSummary({ metrics }: { metrics: NodeMetrics }) {
  const used = diskUsedBytes(metrics)

  if (metrics.disk_total_bytes == null && metrics.disk_free_bytes == null) {
    return <div style={detailStyle}>Usage -</div>
  }

  if (metrics.disk_total_bytes == null) {
    return <div style={detailStyle}>Free {formatBytes(metrics.disk_free_bytes)}</div>
  }

  return (
    <div style={metricStatsGridStyle}>
      <MetricStat
        label="Used"
        value={formatBytes(used)}
        color={metricColor(metrics.disk_percent)}
      />
      <MetricStat label="Free" value={formatBytes(metrics.disk_free_bytes)} />
      <MetricStat label="Total" value={formatBytes(metrics.disk_total_bytes)} />
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      {children}
    </div>
  )
}

export default function ResourcePanel({ metrics }: Props) {
  if (!metrics) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Resources</div>
        <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>No metrics available</p>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <div style={cardTitleStyle}>Resources</div>
      <Row label="CPU">
        <Bar value={metrics.cpu_percent} />
        <div style={detailStyle}>
          {metrics.cpu_count == null ? 'Cores -' : `${metrics.cpu_count} cores`}
        </div>
      </Row>
      <Row label="Memory">
        <Bar value={metrics.memory_percent} />
        <div style={detailStyle}>{memoryDetail(metrics)}</div>
      </Row>
      <Row label="Disk">
        <Bar value={metrics.disk_percent} />
        <DiskSummary metrics={metrics} />
      </Row>
      {metrics.analysis_service_status && (
        <Row label="Analysis service">
          <span style={{ fontSize: 13, color: metrics.analysis_service_status === 'running' ? '#2e7d32' : '#c62828' }}>
            {metrics.analysis_service_status}
          </span>
        </Row>
      )}
      {metrics.collected_at && (
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
          Updated {formatKstTime(metrics.collected_at)}
        </div>
      )}
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '14px 16px',
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#333',
  marginBottom: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const detailStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#666',
}

const metricStatsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 8,
  marginTop: 7,
}

const metricStatStyle: React.CSSProperties = {
  minWidth: 0,
}

const metricStatLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#999',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 2,
}

const metricStatValueStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#333',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}
